import React, { useEffect, useState } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import { PlayIcon } from 'lucide-react';
import { useHasFinePointer, usePrefersReducedMotion } from '../hooks/useMedia';
type CursorMode = 'default' | 'hover' | 'label' | 'play';

/**
 * Elegant desktop cursor. Any element can control it:
 *   data-cursor="hover" | "label" | "play"
 *   data-cursor-label="View Project"
 * Automatically disabled on touch devices and for reduced-motion visitors.
 */
export function CustomCursor() {
  const fine = useHasFinePointer();
  const reduced = usePrefersReducedMotion();
  const enabled = fine && !reduced;
  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const springX = useSpring(x, {
    stiffness: 900,
    damping: 60,
    mass: 0.4
  });
  const springY = useSpring(y, {
    stiffness: 900,
    damping: 60,
    mass: 0.4
  });
  const [mode, setMode] = useState<CursorMode>('default');
  const [label, setLabel] = useState('');
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    document.body.classList.add('cursor-hidden');
    return () => document.body.classList.remove('cursor-hidden');
  }, [enabled]);
  useEffect(() => {
    if (!enabled) return;
    const onMove = (event: MouseEvent) => {
      x.set(event.clientX);
      y.set(event.clientY);
      setVisible(true);
      const target = event.target as HTMLElement | null;
      const node = target?.closest?.('[data-cursor]') as HTMLElement | null;
      if (node) {
        const next = node.dataset.cursor as CursorMode || 'hover';
        setMode(next);
        setLabel(node.dataset.cursorLabel ?? '');
        return;
      }
      const interactive = target?.closest?.('a, button, input, textarea, select');
      setMode(interactive ? 'hover' : 'default');
      setLabel('');
    };
    const onLeave = () => setVisible(false);
    window.addEventListener('mousemove', onMove, {
      passive: true
    });
    window.addEventListener('mouseout', onLeave);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseout', onLeave);
    };
  }, [enabled, x, y]);
  if (!enabled) return null;
  const size = mode === 'label' ? 104 : mode === 'play' ? 76 : mode === 'hover' ? 46 : 12;
  return <motion.div aria-hidden="true" className="pointer-events-none fixed left-0 top-0 z-[90] hidden md:block" style={{
    x: springX,
    y: springY
  }} data-id="element-194" data-mp="1ot3p9v0t170m">
      <motion.div className="flex items-center justify-center rounded-full border text-white" style={{
      translateX: '-50%',
      translateY: '-50%'
    }} animate={{
      width: size,
      height: size,
      opacity: visible ? 1 : 0,
      backgroundColor: mode === 'default' ? '#0511F2' : 'rgba(5,17,242,0.92)',
      borderColor: mode === 'default' ? '#0511F2' : 'rgba(201,203,242,0.6)'
    }} transition={{
      type: 'spring',
      stiffness: 320,
      damping: 26,
      mass: 0.5
    }} data-id="element-195" data-mp="1fwutqrfy6sjr">
        {mode === 'label' && label ? <span className="px-2 text-center font-mono text-[9px] uppercase leading-tight tracking-[0.14em]" data-id="element-196" data-mp="ic30jxi7du98">
            {label}
          </span> : null}
        {mode === 'play' ? <PlayIcon className="h-4 w-4 fill-white" data-id="element-197" data-mp="rdaz8gomsx3u" /> : null}
      </motion.div>
    </motion.div>;
}